import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';

const MarkdownRenderer = ({ content, className = '' }) => {
  const components = {
    // Заголовки
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-foreground mt-6 mb-4 pb-2 border-b-2 border-border first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-foreground mt-5 mb-3 pb-1 border-b border-border">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-semibold text-foreground mt-3 mb-2">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="text-sm font-semibold text-muted-foreground mt-3 mb-1">
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 className="text-sm font-semibold text-muted-foreground mt-2 mb-1">
        {children}
      </h6>
    ),
    
    // Параграфы
    p: ({ children }) => (
      <p className="my-3 leading-relaxed text-foreground first:mt-0 last:mb-0">
        {children}
      </p>
    ),
    
    // Списки
    ul: ({ children }) => (
      <ul className="my-3 pl-6 space-y-1 list-disc marker:text-muted-foreground">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-3 pl-6 space-y-1 list-decimal marker:text-muted-foreground">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="leading-relaxed text-foreground">
        {children}
      </li>
    ),
    
    // Таблицы
    table: ({ children }) => (
      <Card className="my-4 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            {children}
          </table>
        </div>
      </Card>
    ),
    thead: ({ children }) => (
      <thead className="bg-muted/50">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody>
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="border-b border-border last:border-b-0 even:bg-muted/20">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="p-3 text-left font-semibold text-foreground border-r border-border last:border-r-0">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="p-3 text-foreground border-r border-border last:border-r-0 align-top">
        {children}
      </td>
    ),
    
    // Блоки кода
    code: ({ node, inline, className, children, ...props }) => {
      if (inline) {
        return (
          <code className="bg-muted text-foreground px-1.5 py-0.5 rounded text-sm font-mono border" {...props}>
            {children}
          </code>
        );
      }
      return (
        <Card className="my-4">
          <CardContent className="p-0">
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
              <code className="font-mono text-sm leading-relaxed" {...props}>
                {children}
              </code>
            </pre>
          </CardContent>
        </Card>
      );
    },
    
    // Цитаты
    blockquote: ({ children }) => (
      <Card className="my-4 border-l-4 border-l-primary bg-primary/5">
        <CardContent className="p-4">
          <div className="text-primary italic">
            {children}
          </div>
        </CardContent>
      </Card>
    ),
    
    // Ссылки
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    
    // Жирный и курсивный текст
    strong: ({ children }) => (
      <strong className="font-semibold text-foreground">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-muted-foreground">
        {children}
      </em>
    ),
    
    // Горизонтальная линия
    hr: () => <Separator className="my-6" />,
    
    // Изображения (если понадобятся)
    img: ({ src, alt }) => (
      <Card className="my-4 overflow-hidden">
        <img 
          src={src} 
          alt={alt} 
          className="w-full h-auto rounded-lg" 
        />
      </Card>
    )
  };

  return (
    <div className={`prose prose-slate max-w-none text-sm leading-relaxed ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
